/**
 * useSignOut — end the session.
 *
 * ONE dispatch does the whole job. `signedOut` clears the identity and roles in
 * the slice, and the listener in app/listenerMiddleware.js reacts by dropping
 * the in-memory token (which also removes every mirrored cookie) and resetting
 * every RTK Query cache entry, so no previous user's data can be served from
 * cache to the next one.
 *
 * That single seam is the point. Legacy spreads the same job across four
 * hand-maintained sites that have already drifted apart:
 *   Headerbar.vue:568-582   14 cookies, localStorage only
 *   apiservice.js:176-198   15-entry array (access_token listed twice), both storages
 *   apiservice.js:217-238   the same array again, on refresh failure
 *   apiservice.js:278-294   15 individual removes, on 401
 * All four forget the `domain` cookie that Login.vue:164 writes, so the
 * previous user's organisation survives logout for a day. A list that cannot
 * drift is the fix, not a fifth copy of the list — this app writes and removes
 * the same cookie set from one constant in security/auth/sessionMirror.js.
 *
 * On the Advisory sign-in the browser goes STRAIGHT to `VITE_OKTA_LOGOUT_LINK` +
 * `&appName=CCA|EIA` — Common Services' sign-out — and this app clears nothing
 * first. Common Services removes the same shared `*.amd.com` session cookies
 * itself and sends the user back to this product (UI-Common-Services
 * rootLoader.js), so clearing here, showing a toast and routing to '/' first
 * only added a visible delay and a flash of the sign-in page before the hand-
 * off. The page is about to be left, so its in-memory state goes with it.
 *
 * Only when there is nowhere to hand over to — the local form (localhost; no
 * Common Services session to end) or no logout link configured — does it sign
 * out in place, as legacy (Headerbar.vue:561-586): clear, green "User logged out
 * successfully" toast, and the page moves to '/' with `replace: true`, so Back
 * cannot return to the page just left.
 *
 * Either way, first, the page on screen may ask "discard unsaved changes?"
 * (router/leaveGuard.js) — legacy's Headerbar redirect('/logout') asked too. No
 * keeps the user signed in. It must be asked BEFORE anything is cleared.
 */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { buildCsLogoutUrl } from '@config/csLogout';
import { env } from '@config/env';
import { getLoginFlags } from '@config/loginMode';
import { useNotify } from '@hooks/useNotify';
import { confirmLeave } from '@router/leaveGuard';
import { signedOut } from '@security/auth/authSlice';
import { redirectTo } from '@utils/navigation';
import { getVariant } from '@variant/appMode';

/** Legacy's snackbar text, verbatim. */
export const SIGNED_OUT_MESSAGE = 'User logged out successfully';

export function useSignOut() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notify = useNotify();

  return useCallback(async () => {
    if (!(await confirmLeave())) return;

    // Common Services' ?state=logout, naming this product so it sends the user
    // back here once its side is cleared (config/csLogout.js). getVariant() is
    // the resolved product without needing the AppModeProvider context.
    const logoutUrl = getLoginFlags().advisoryLogin
      ? buildCsLogoutUrl(env.csLogoutUrl, getVariant().shortName)
      : null;
    if (logoutUrl) {
      redirectTo(logoutUrl); // Common Services clears the session; nothing to do here
      return;
    }

    dispatch(signedOut());
    navigate('/', { replace: true });
    notify.success(SIGNED_OUT_MESSAGE);
  }, [dispatch, navigate, notify]);
}
